/**
 * SocialPreneur — the "About SocialPreneur / Our Mission" page, mounted at
 * `/about` via TENANT_ROUTES (so it exists in information-site-only mode too,
 * where App.tsx serves TENANT_ROUTES and nothing else).
 *
 * WHY A PAGE AND NOT LANDING SECTIONS: the design handoff covers Asta Cafe the
 * PRODUCT — there is no About or Mission screen in it. Rather than graft
 * company copy onto a product landing that was composed as one long argument
 * for the blend, the company story gets its own page and the landing is left
 * alone.
 *
 * All copy here is DRAFT and lives in the site-content manifest (`about.*`), so
 * the client replaces it in Admin → Storefront → Site Pages without a deploy.
 * The look reuses the landing's gold/espresso tokens + `asta.css` — this is the
 * same Layer-3 bespoke exception to "no hardcoded colors" documented there.
 */
import { SOCIALPRENEUR_SITE } from '@mlm/shared';
import { ArrowRight } from 'lucide-react';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { renderRichParas } from '../../../storefront/lib/rich-paras';
import { AstaShell } from './shell';
import { FONT_DISPLAY, FONT_BODY, FONT_SCRIPT } from './tokens';

export function AstaAbout(): JSX.Element {
  return (
    <AstaShell>
      <AstaAboutBody />
    </AstaShell>
  );
}

function AstaAboutBody(): JSX.Element {
  const sc = useSiteContent(SOCIALPRENEUR_SITE);
  const pillars = sc.items('about.mission.pillars');
  // Shared with the landing's Contact band — an emptied field hides the button
  // rather than shipping a dead `href="mailto:"`.
  const contactEmail = sc.text('landing.cta.contactEmail').trim();
  return (
    <div style={{ fontFamily: FONT_BODY }}>
      {/* ── Page header (dark) ── */}
      <section style={{ background: 'radial-gradient(120% 120% at 50% 0%, #2c1c0d, #150d06)', borderBottom: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '86px 24px 74px', textAlign: 'center' }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold)' }}>
            {sc.text('about.hero.eyebrow')}
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(34px,5.2vw,60px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 0', color: 'var(--cream)' }}>
            {sc.text('about.hero.title')}
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--muted)', maxWidth: '48ch', margin: '18px auto 0' }}>
            {sc.text('about.hero.sub')}
          </p>
        </div>
      </section>

      {/* ── Who we are (cream) ── */}
      <section id="story" style={{ scrollMarginTop: 96, background: 'var(--cream-2)', color: 'var(--espresso)' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', padding: '84px 24px' }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(28px,4vw,44px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '0 0 22px', color: 'var(--espresso)' }}>
            {sc.text('about.story.title')}
          </h2>
          <div className="asta-prose" style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--muted-2)' }}>
            {renderRichParas(sc.paras('about.story.body'))}
          </div>
        </div>
      </section>

      {/* ── Our mission (dark) ── */}
      <section id="mission" style={{ scrollMarginTop: 80, background: 'linear-gradient(180deg,#1d130a,#160e06)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '84px 24px' }}>
          <div style={{ maxWidth: 720 }}>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(28px,4vw,44px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '0 0 20px', color: 'var(--cream)' }}>
              {sc.text('about.mission.title')}
            </h2>
            <div className="asta-prose" style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--muted)' }}>
              {renderRichParas(sc.paras('about.mission.body'))}
            </div>
          </div>
          {pillars.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20, marginTop: 44 }}>
              {pillars.map((p, i) => (
                <div key={p.title || i} style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 16, padding: 28 }}>
                  <div style={{ fontFamily: FONT_SCRIPT, fontSize: 30, color: 'var(--gold-l)', lineHeight: 1, marginBottom: 10 }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 19, letterSpacing: '.03em', margin: '0 0 8px', color: 'var(--cream)' }}>
                    {p.title}
                  </h3>
                  <p style={{ fontSize: 14.5, lineHeight: 1.65, color: 'var(--muted)', margin: 0 }}>{p.desc}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Closing CTA (cream) — the mailto is the only action, same as the
             landing's Contact band. ── */}
      <section style={{ background: 'var(--cream-2)', color: 'var(--espresso)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '76px 24px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(26px,3.6vw,40px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: 0, color: 'var(--espresso)' }}>
            {sc.text('about.cta.title')}
          </h2>
          <p style={{ fontSize: 16.5, lineHeight: 1.7, color: 'var(--muted-2)', maxWidth: '46ch', margin: '16px auto 0' }}>
            {sc.text('about.cta.body')}
          </p>
          {contactEmail && (
          <a
            href={`mailto:${contactEmail}`}
            className="asta-btn-gold"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              marginTop: 28,
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: 15,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: '#2a1b0e',
              background: 'var(--gold-grad)',
              padding: '15px 28px',
              borderRadius: 11,
              boxShadow: '0 8px 22px rgba(200,148,42,.28)',
            }}
          >
            {sc.text('about.cta.cta')} <ArrowRight size={15} strokeWidth={2.4} />
          </a>
          )}
        </div>
      </section>
    </div>
  );
}
